// ═══════════════════════════════════════════════════════════
// RBAC Store — Roles + UserRoles backed by V2 API
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from '../auth/auth-store';
import { DEFAULT_ROLES } from './rbac-defaults';
import { computeAccess, type ModuleId, type ModuleAccess, type PermissionLevel, type Role, type UserRole, type RbacPermissions } from './rbac-types';

const RF = FIELDS.roles;
const UF = FIELDS.userRoles;

// ── Field Mappers ────────────────────────────────────────

function mapRole(row: Record<string, unknown>): Role {
  let perms: RbacPermissions;
  try {
    const raw = row[RF.permissions];
    perms = typeof raw === 'string' ? JSON.parse(raw) : (raw as RbacPermissions);
  } catch {
    perms = {} as RbacPermissions;
  }
  return {
    id: row.record_id as number,
    roleName: (row[RF.roleName] as string) || '',
    roleSlug: (row[RF.roleSlug] as string) || '',
    description: (row[RF.description] as string) || '',
    permissions: perms,
    isDefault: String(row[RF.isDefault]) === 'true',
    sortOrder: Number(row[RF.sortOrder]) || 99,
  };
}

function rolePayload(data: Partial<Omit<Role, 'id'>>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.roleName !== undefined) p[RF.roleName] = data.roleName;
  if (data.roleSlug !== undefined) p[RF.roleSlug] = data.roleSlug;
  if (data.description !== undefined) p[RF.description] = data.description;
  if (data.permissions !== undefined) p[RF.permissions] = JSON.stringify(data.permissions);
  if (data.isDefault !== undefined) p[RF.isDefault] = String(data.isDefault);
  if (data.sortOrder !== undefined) p[RF.sortOrder] = data.sortOrder;
  return p;
}

function mapUserRole(row: Record<string, unknown>): UserRole {
  return {
    id: row.record_id as number,
    userEmail: (row[UF.userEmail] as string) || '',
    userName: (row[UF.userName] as string) || '',
    roleId: (row[UF.roleId] as string) || '',
    roleName: (row[UF.roleName] as string) || '',
    assignedBy: (row[UF.assignedBy] as string) || '',
  };
}

function userRolePayload(data: { userEmail: string; userName: string; roleId: string; roleName: string; assignedBy?: string }): Record<string, unknown> {
  return {
    [UF.userEmail]: data.userEmail,
    [UF.userName]: data.userName,
    [UF.roleId]: data.roleId,
    [UF.roleName]: data.roleName,
    [UF.assignedBy]: data.assignedBy || '',
    [UF.assignedAt]: new Date().toISOString(),
  };
}

// ── Store ────────────────────────────────────────────────

interface RbacState {
  roles: Role[];
  userRoles: UserRole[];
  myRole: Role | null;
  loading: boolean;

  // Actions
  loadRoles: () => Promise<void>;
  loadUserRoles: () => Promise<void>;
  resolveMyRole: () => void;
  getModuleAccess: (moduleId: ModuleId) => ModuleAccess;

  // CRUD — Roles
  createRole: (data: Omit<Role, 'id'>) => Promise<void>;
  updateRole: (id: number, data: Partial<Omit<Role, 'id'>>) => Promise<void>;
  deleteRole: (id: number) => Promise<void>;

  // CRUD — UserRoles
  assignRole: (userEmail: string, userName: string, roleId: string, roleName: string) => Promise<void>;
  removeAssignment: (id: number) => Promise<void>;
}

export const useRbacStore = create<RbacState>((set, get) => ({
  roles: [],
  userRoles: [],
  myRole: null,
  loading: false,

  loadRoles: async () => {
    set({ loading: true });
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.roles, {
        limit: 200,
        sort: { field: RF.sortOrder, direction: 'asc' },
      });
      const roles = res.records.map(mapRole);

      // If no roles in DB yet, seed from defaults
      if (roles.length === 0) {
        const seeded: Role[] = DEFAULT_ROLES.map((r, i) => ({
          id: i + 1,
          roleName: r.roleName,
          roleSlug: r.roleSlug,
          description: r.description,
          permissions: r.permissions,
          isDefault: r.isDefault,
          sortOrder: r.sortOrder,
        }));
        set({ roles: seeded });
      } else {
        set({ roles });
      }
    } catch (e) {
      console.error('[RBAC] loadRoles', e);
      // Fall back to defaults
      const seeded: Role[] = DEFAULT_ROLES.map((r, i) => ({
        id: i + 1,
        roleName: r.roleName,
        roleSlug: r.roleSlug,
        description: r.description,
        permissions: r.permissions,
        isDefault: r.isDefault,
        sortOrder: r.sortOrder,
      }));
      set({ roles: seeded });
    }
    set({ loading: false });
  },

  loadUserRoles: async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.userRoles, { limit: 500 });
      set({ userRoles: res.records.map(mapUserRole) });
    } catch (e) {
      console.error('[RBAC] loadUserRoles', e);
      set({ userRoles: [] });
    }
  },

  resolveMyRole: () => {
    const user = useAuthStore.getState().user;
    if (!user) { set({ myRole: null }); return; }

    const assignment = get().userRoles.find(
      ur => ur.userEmail.toLowerCase() === user.email.toLowerCase()
    );

    if (assignment) {
      const role = get().roles.find(r => String(r.id) === assignment.roleId);
      if (role) { set({ myRole: role }); return; }
    }

    if (user.isAdmin) {
      const adminRole = get().roles.find(r => r.roleSlug === 'super_admin');
      if (adminRole) { set({ myRole: adminRole }); return; }
    }

    const defaultRole = get().roles.find(r => r.isDefault);
    set({ myRole: defaultRole || null });
  },

  getModuleAccess: (moduleId) => {
    const role = get().myRole;
    if (!role) return computeAccess('none');

    const user = useAuthStore.getState().user;
    if (user?.isAdmin) return computeAccess('admin');

    const level: PermissionLevel = role.permissions[moduleId] || 'none';
    return computeAccess(level);
  },

  // ── Role CRUD ──────────────────────────────────────────

  createRole: async (data) => {
    try {
      await createRecord(TABLES.roles, rolePayload(data));
      await get().loadRoles();
    } catch (e) {
      console.error('[RBAC] createRole', e);
      throw e;
    }
  },

  updateRole: async (id, data) => {
    try {
      await updateRecord(TABLES.roles, id, rolePayload(data));
      await get().loadRoles();
    } catch (e) {
      console.error('[RBAC] updateRole', e);
      throw e;
    }
  },

  deleteRole: async (id) => {
    try {
      await deleteRecord(TABLES.roles, id);
      await get().loadRoles();
    } catch (e) {
      console.error('[RBAC] deleteRole', e);
      throw e;
    }
  },

  // ── UserRole CRUD ──────────────────────────────────────

  assignRole: async (userEmail, userName, roleId, roleName) => {
    try {
      // Check for existing assignment — update instead of duplicate
      const existing = get().userRoles.find(ur => ur.userEmail.toLowerCase() === userEmail.toLowerCase());
      const assignedBy = useAuthStore.getState().user?.email || '';
      if (existing) {
        await updateRecord(TABLES.userRoles, existing.id, userRolePayload({ userEmail, userName, roleId, roleName, assignedBy }));
      } else {
        await createRecord(TABLES.userRoles, userRolePayload({ userEmail, userName, roleId, roleName, assignedBy }));
      }
      await get().loadUserRoles();
    } catch (e) {
      console.error('[RBAC] assignRole', e);
      throw e;
    }
  },

  removeAssignment: async (id) => {
    try {
      await deleteRecord(TABLES.userRoles, id);
      await get().loadUserRoles();
    } catch (e) {
      console.error('[RBAC] removeAssignment', e);
      throw e;
    }
  },
}));
